import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import { SubCategoryForm } from "../sub-categories/sub-category-form";

import { FormError, FormModal } from "@/components/kit";
import { useCreateSubCategory } from "@/hooks/api/category/useCreateSubCategory";
import {
  SUB_CATEGORY_FORM_SCHEMA,
  SubCategoryFormData,
} from "@/schemas/subcategory-form.schema.";

interface AddSubCategoryModalProps {
  categoryId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSubCategoryModal({
  categoryId,
  isOpen,
  onClose,
  onSuccess,
}: AddSubCategoryModalProps) {
  const {
    mutate: createSubCategory,
    isPending: isCreatingSubCategory,
    isError,
    error,
  } = useCreateSubCategory(categoryId);
  const form = useForm<SubCategoryFormData>({
    resolver: zodResolver(SUB_CATEGORY_FORM_SCHEMA),
  });

  // Auto-generate ID from name
  const generateId = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleSubmit = (values: SubCategoryFormData) => {
    createSubCategory(values, {
      onSuccess: () => {
        onSuccess();
      },
    });
  };

  const name = form.watch("name");

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "Failed to create sub category, please try again"
    : null;

  useEffect(() => {
    if (name) {
      form.setValue("id", generateId(name));
    }
  }, [name]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isCreatingSubCategory}
      submitLabel="Create Subcategory"
      title="Add New Subcategory"
      onClose={onClose}
      onSubmit={() => form.handleSubmit(handleSubmit)()}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <SubCategoryForm />
      </FormProvider>
    </FormModal>
  );
}
